import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import RichEditor from "@/components/RichEditor";
import ImageUploadField from "@/components/ImageUploadField";
import MediaUploadField from "@/components/MediaUploadField";

const empty = { title: "", excerpt: "", content: "", cover_image: "", audio_url: "", video_url: "", published: true, locked: true };

export default function HealingAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/healing/all").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (e) => {
    setForm({
      title: e.title, excerpt: e.excerpt || "", content: e.content || "", cover_image: e.cover_image || "",
      audio_url: e.audio_url || "", video_url: e.video_url || "", published: !!e.published, locked: !!e.locked,
    });
    setEditing(e.id);
  };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Add a title first.");
    setSaving(true);
    try {
      if (editing === "new") await api.post("/healing", form);
      else await api.put(`/healing/${editing}`, form);
      toast.success("Entry saved");
      await load();
      cancel();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not save.")); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/healing/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <AdminLayout title="Healing Space">
      <Toaster position="top-center" />
      <div className="flex justify-between items-center flex-wrap gap-4">
        <p className="text-[#5C6670] font-light">Audio, video and writing for the Space for Healing Together — lock any entry to keep it members-only.</p>
        <button onClick={startNew} className="gv-btn-gold" data-testid="healing-new">+ New entry</button>
      </div>

      {editing && (
        <div data-testid="healing-form" className="mt-8 bg-white border border-[#C5A059] p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="gv-label">Title</label>
            <input className="gv-input mt-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="healing-title" />
          </div>

          <label className="flex items-center gap-3 text-sm text-[#1A1F24]">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="healing-published" />
            <span>Published</span>
          </label>
          <label className="flex items-center gap-3 text-sm text-[#1A1F24]">
            <input type="checkbox" checked={form.locked} onChange={(e) => setForm({ ...form, locked: e.target.checked })} data-testid="healing-locked" />
            <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#C5A059]" /> Exclusive (members only)</span>
          </label>

          <div className="md:col-span-2">
            <ImageUploadField value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="Cover image" testIdPrefix="healing-cover" aspect="aspect-[16/10]" />
          </div>
          <MediaUploadField value={form.audio_url} onChange={(url) => setForm({ ...form, audio_url: url })} kind="audio" testIdPrefix="healing-audio" />
          <MediaUploadField value={form.video_url} onChange={(url) => setForm({ ...form, video_url: url })} kind="video" testIdPrefix="healing-video" />

          <div className="md:col-span-2">
            <label className="gv-label">Excerpt</label>
            <textarea rows={2} className="gv-input mt-2 resize-none" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="healing-excerpt" />
          </div>
          <div className="md:col-span-2">
            <label className="gv-label">Content</label>
            <div className="mt-2"><RichEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} /></div>
          </div>
          <div className="md:col-span-2 flex gap-3 justify-end">
            <button onClick={cancel} className="gv-btn-outline" data-testid="healing-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="gv-btn-gold" data-testid="healing-save">{saving ? "Saving…" : "Save entry"}</button>
          </div>
        </div>
      )}

      <div className="mt-12 divide-y divide-[#E5E5E5]">
        {items.map((e) => (
          <div key={e.id} data-testid={`admin-healing-${e.id}`} className="py-5 flex items-start gap-4">
            <div className="w-16 h-16 flex-shrink-0 bg-[#F5F5F5] overflow-hidden">
              {e.cover_image && <img src={e.cover_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059] flex items-center gap-2">
                {e.locked && <span className="inline-flex items-center gap-1 text-[#C5A059]"><Lock className="w-3 h-3" /> Exclusive</span>}
                {!e.published && <span className="text-[#B8BCC2]">· Draft</span>}
                {e.audio_url && <span className="text-[#B8BCC2]">· Audio</span>}
                {e.video_url && <span className="text-[#B8BCC2]">· Video</span>}
              </div>
              <div className="font-serif text-xl text-[#1A1F24] mt-1">{e.title}</div>
              <p className="text-sm text-[#5C6670] mt-1 line-clamp-2">{e.excerpt}</p>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <button onClick={() => startEdit(e)} className="text-xs tracking-[0.2em] uppercase text-[#C5A059] gv-link" data-testid={`admin-healing-edit-${e.id}`}>Edit</button>
              <button onClick={() => remove(e.id)} className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-red-500" data-testid={`admin-healing-delete-${e.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
